import Phaser from 'phaser';
import { BRAND, HEX } from '../config';
import { LEVELS, ITEMS, isPanel, isInverter } from '../game/levels';
import type { Item, Level } from '../game/levels';
import { PanelSprite } from '../game/Panel';
import { azimuthToSunVector } from '../game/Sun';

type SceneData = { levelIndex: number };

import { presentLogical } from '../hidpi';

export class BriefingScene extends Phaser.Scene {
  private level!: Level;
  private levelIndex = 0;

  constructor() {
    super('BriefingScene');
  }

  init(data: SceneData) {
    this.levelIndex = data.levelIndex ?? 0;
    this.level = LEVELS[this.levelIndex];
  }

  create() {
    const { width, height } = presentLogical(this);

    // Soft sky-to-ink background
    this.cameras.main.setBackgroundColor(BRAND.ink);
    this.cameras.main.fadeIn(220, 0x2a, 0x31, 0x40);
    const grad = this.add.graphics();
    grad.fillGradientStyle(BRAND.skyTop, BRAND.skyTop, BRAND.ink, BRAND.ink, 1, 1, 0, 0);
    grad.fillRect(0, 0, width, height);

    // Top bar — logo + level number
    const topY = 18;
    const logo = this.add.image(28, topY + 14, 'sj-logo').setOrigin(0, 0.5);
    logo.setScale(28 / logo.height);
    const dividerX = 28 + logo.displayWidth + 14;
    const div = this.add.graphics();
    div.fillStyle(0xffffff, 0.18);
    div.fillRect(dividerX, topY + 4, 1, 22);
    this.add.text(dividerX + 14, topY + 4, 'BRIEFING', {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '11px',
      color: HEX.amber,
      fontStyle: 'bold',
    }).setLetterSpacing(2);
    this.add.text(dividerX + 14, topY + 18, `LEVEL ${String(this.level.id).padStart(2, '0')}`, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '12px',
      color: HEX.greyDim,
      fontStyle: 'bold',
    });

    // Back link top right
    this.makeTextLink(width - 28, topY + 14, '← MENU', () => this.scene.start('MenuScene'));

    // Title
    this.add
      .text(width / 2, 76, this.level.title.replace(/^\d+\s*[—-]\s*/, ''), {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '34px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add
      .text(width / 2, 110, this.level.hint, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: HEX.amberSoft,
        wordWrap: { width: width - 80 },
        align: 'center',
      })
      .setOrigin(0.5);

    // === Top stat row: Power | Storage | Budget | Sun ===
    const rowY = 156;
    const rowH = 110;
    const colGap = 10;
    const rowMargin = 32;
    const totalRowW = width - rowMargin * 2;
    const colW = (totalRowW - colGap * 3) / 4;

    this.drawStatCard(
      rowMargin,
      rowY,
      colW,
      rowH,
      'POWER TARGET',
      `${this.level.targetKW.toFixed(1)}`,
      'kW',
      BRAND.amber,
    );
    this.drawStatCard(
      rowMargin + (colW + colGap),
      rowY,
      colW,
      rowH,
      'STORAGE TARGET',
      `${this.level.targetKWh}`,
      'kWh',
      0x57c47a,
    );
    this.drawStatCard(
      rowMargin + (colW + colGap) * 2,
      rowY,
      colW,
      rowH,
      'BUDGET',
      `$${this.level.budget.toLocaleString()}`,
      '',
      BRAND.white,
    );
    this.drawSunCard(rowMargin + (colW + colGap) * 3, rowY, colW, rowH);

    // === Available items ===
    const panelsY = rowY + rowH + 16;
    this.add
      .text(rowMargin, panelsY, 'WHAT YOU HAVE TO WORK WITH', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);
    const panelsListY = panelsY + 20;
    const items = this.level.inventory.map((it) => ({ inv: it, item: ITEMS[it.type] }));
    // Wrap into a grid: 5 per row max, two rows fits the full catalogue.
    const perRow = Math.min(items.length, 5);
    const rows = Math.ceil(items.length / perRow);
    const cardGap = 8;
    const panelCardW = (totalRowW - cardGap * (perRow - 1)) / perRow;
    const panelCardH = 100;
    items.forEach(({ inv, item }, i) => {
      const r = Math.floor(i / perRow);
      const c = i % perRow;
      this.drawPanelCard(
        rowMargin + c * (panelCardW + cardGap),
        panelsListY + r * (panelCardH + 6),
        panelCardW,
        panelCardH,
        item,
        inv.count,
      );
    });

    // === Controls strip ===
    const ctrlY = panelsListY + rows * panelCardH + (rows - 1) * 6 + 8;
    this.drawControlsStrip(rowMargin, ctrlY, totalRowW, 30);

    // === Bottom buttons ===
    const btnY = height - 64;
    const btnH = 44;
    this.makeButton(
      'BACK',
      rowMargin,
      btnY,
      130,
      btnH,
      BRAND.greyDark,
      () => this.scene.start('MenuScene'),
    );
    this.makeButton(
      'START LEVEL  →',
      width - rowMargin - 220,
      btnY,
      220,
      btnH,
      BRAND.red,
      () => this.scene.start('LevelScene', { levelIndex: this.levelIndex }),
    );
  }

  /**
   * Horizontal control-reference bar. Each item is a key/icon chip + a label.
   * Used in the briefing so first-time players see how to play.
   */
  private drawControlsStrip(x: number, y: number, w: number, h: number) {
    const items: { key: string; label: string }[] = [
      { key: 'CLICK', label: 'Place panel' },
      { key: 'R', label: 'Rotate footprint' },
      { key: 'SHIFT+CLICK', label: 'Remove panel' },
      { key: 'HOVER', label: 'See sun %' },
      { key: 'ESC', label: 'Back to menu' },
    ];

    const bg = this.add.graphics();
    bg.fillStyle(BRAND.inkSoft, 1);
    bg.fillRoundedRect(x, y, w, h, 8);
    bg.lineStyle(1, 0xffffff, 0.07);
    bg.strokeRoundedRect(x + 0.5, y + 0.5, w - 1, h - 1, 8);

    // Distribute items evenly across the bar
    const itemW = w / items.length;
    items.forEach((it, i) => {
      const cx = x + i * itemW + 12;
      this.drawKeyChip(cx, y + h / 2, it.key);
      const chipW = this.measureKeyChip(it.key);
      this.add
        .text(cx + chipW + 8, y + h / 2, it.label, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: HEX.greyDim,
        })
        .setOrigin(0, 0.5);
      // Divider between items
      if (i > 0) {
        bg.fillStyle(0xffffff, 0.06);
        bg.fillRect(x + i * itemW, y + 6, 1, h - 12);
      }
    });
  }

  private drawKeyChip(x: number, cy: number, label: string) {
    const padX = 6;
    const tmp = this.add
      .text(0, 0, label, { fontFamily: 'Albert Sans, sans-serif', fontSize: '10px' })
      .setVisible(false);
    const w = tmp.width + padX * 2;
    const h = 16;
    tmp.destroy();
    const g = this.add.graphics();
    g.fillStyle(0x141821, 1);
    g.fillRoundedRect(x, cy - h / 2, w, h, 3);
    g.lineStyle(1, BRAND.amber, 0.55);
    g.strokeRoundedRect(x + 0.5, cy - h / 2 + 0.5, w - 1, h - 1, 3);
    this.add
      .text(x + w / 2, cy, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(1);
  }

  private measureKeyChip(label: string): number {
    const tmp = this.add
      .text(0, 0, label, { fontFamily: 'Albert Sans, sans-serif', fontSize: '10px' })
      .setVisible(false);
    const w = tmp.width + 12;
    tmp.destroy();
    return w;
  }

  // ────────────────────────────────────────────────────────────────────────────
  private drawStatCard(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    value: string,
    unit: string,
    valueColor: number,
  ) {
    const g = this.add.graphics();
    g.fillStyle(BRAND.inkSoft, 1);
    g.fillRoundedRect(x, y, w, h, 10);
    g.lineStyle(1, 0xffffff, 0.07);
    g.strokeRoundedRect(x + 0.5, y + 0.5, w - 1, h - 1, 10);
    // Top accent band
    g.fillStyle(valueColor, 0.85);
    g.fillRoundedRect(x, y, w, 4, { tl: 10, tr: 10, bl: 0, br: 0 });

    this.add
      .text(x + 18, y + 18, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);

    const valueText = this.add.text(x + 14, y + h - 44, value, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '34px',
      color: '#' + valueColor.toString(16).padStart(6, '0'),
      fontStyle: 'bold',
    });
    if (unit) {
      this.add.text(x + 14 + valueText.width + 6, y + h - 24, unit, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      });
    }
  }

  private drawSunCard(x: number, y: number, w: number, h: number) {
    const g = this.add.graphics();
    g.fillStyle(BRAND.inkSoft, 1);
    g.fillRoundedRect(x, y, w, h, 10);
    g.lineStyle(1, 0xffffff, 0.07);
    g.strokeRoundedRect(x + 0.5, y + 0.5, w - 1, h - 1, 10);
    g.fillStyle(BRAND.orange, 0.85);
    g.fillRoundedRect(x, y, w, 4, { tl: 10, tr: 10, bl: 0, br: 0 });

    this.add
      .text(x + 14, y + 14, 'SUN DIRECTION', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);

    // Mini compass on left, label on right
    const cx = x + 42;
    const cy = y + h / 2 + 8;
    const r = 24;
    g.lineStyle(1.5, BRAND.greyDark, 1);
    g.strokeCircle(cx, cy, r);
    g.lineStyle(1, BRAND.greyDark, 0.4);
    g.strokeCircle(cx, cy, r * 0.55);

    const cardOpts = {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '9px',
      color: HEX.greyDim,
    };
    this.add.text(cx, cy - r - 8, 'N', cardOpts).setOrigin(0.5);
    this.add.text(cx + r + 8, cy, 'E', cardOpts).setOrigin(0.5);
    this.add.text(cx, cy + r + 8, 'S', cardOpts).setOrigin(0.5);
    this.add.text(cx - r - 8, cy, 'W', cardOpts).setOrigin(0.5);

    const v = azimuthToSunVector(this.level.sunAzimuthDeg);
    const sunX = cx + v.x * r;
    const sunY = cy + v.y * r;
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(BRAND.amber, 0.07 * i);
      g.fillCircle(sunX, sunY, 6 + i * 3);
    }
    g.fillStyle(BRAND.amber, 1);
    g.fillCircle(sunX, sunY, 7);

    // Direction line toward centre
    g.lineStyle(2, BRAND.orange, 1);
    g.lineBetween(sunX, sunY, cx - v.x * (r * 0.4), cy - v.y * (r * 0.4));

    // Cardinal label on right side
    const cardinal = this.cardinalLabel(this.level.sunAzimuthDeg);
    this.add.text(x + 80, y + h / 2 - 4, cardinal, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '24px',
      color: HEX.amber,
      fontStyle: 'bold',
    });
    this.add.text(x + 80, y + h / 2 + 22, `${this.level.sunAzimuthDeg}°`, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '11px',
      color: HEX.greyDim,
    });
  }

  private cardinalLabel(deg: number): string {
    const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return names[Math.round(deg / 45) % 8];
  }

  private drawPanelCard(
    x: number,
    y: number,
    w: number,
    h: number,
    item: Item,
    count: number,
  ) {
    const roleColor = isPanel(item) ? BRAND.amber : isInverter(item) ? 0x9aa4b0 : 0x57c47a;
    const roleHex = '#' + roleColor.toString(16).padStart(6, '0');
    const roleLabel = isPanel(item) ? 'PANEL' : isInverter(item) ? 'INVERTER' : 'BATTERY';

    // Body
    const g = this.add.graphics();
    g.fillStyle(BRAND.inkSoft, 1);
    g.fillRoundedRect(x, y, w, h, 8);
    g.lineStyle(1, 0xffffff, 0.07);
    g.strokeRoundedRect(x + 0.5, y + 0.5, w - 1, h - 1, 8);
    // Top accent strip
    g.fillStyle(roleColor, 0.9);
    g.fillRoundedRect(x, y, w, 3, { tl: 8, tr: 8, bl: 0, br: 0 });

    // ── Header row: role tag (left) + count chip (right) ──
    this.add
      .text(x + 8, y + 9, roleLabel, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '8px',
        color: roleHex,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);
    // Count chip at top-right
    const chipText = `×${count}`;
    const tmp = this.add
      .text(0, 0, chipText, { fontFamily: 'Albert Sans, sans-serif', fontSize: '10px' })
      .setVisible(false);
    const chipW = tmp.width + 8;
    tmp.destroy();
    const chipH = 14;
    const chipX = x + w - chipW - 6;
    const chipY = y + 6;
    const chipBg = this.add.graphics();
    chipBg.fillStyle(0x141821, 1);
    chipBg.fillRoundedRect(chipX, chipY, chipW, chipH, 3);
    chipBg.lineStyle(1, BRAND.amber, 0.6);
    chipBg.strokeRoundedRect(chipX + 0.5, chipY + 0.5, chipW - 1, chipH - 1, 3);
    this.add
      .text(chipX + chipW / 2, chipY + chipH / 2, chipText, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // ── Name on its own line ──
    this.add
      .text(x + 8, y + 22, item.name, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '12px',
        color: HEX.white,
        fontStyle: 'bold',
      });

    // ── Preview centred in fixed region ──
    const regionTop = y + 40;
    const regionH = 42;
    const previewMaxW = w - 16;
    const previewMaxH = regionH - 4;
    const longAxis = Math.max(item.width, item.height);
    const cellPx = Math.min(previewMaxH / longAxis, previewMaxW / longAxis, 20);
    const drawW = item.width * cellPx;
    const drawH = item.height * cellPx;
    const drawX = x + (w - drawW) / 2;
    const drawY = regionTop + (regionH - drawH) / 2;
    const previewG = this.add.graphics();
    PanelSprite.render(previewG, null, item, drawX, drawY, drawW, drawH, 180);

    // ── Spec line at fixed Y, compact format ──
    let spec: string;
    if (isPanel(item)) {
      spec = `${item.watts}W · $${item.cost}`;
    } else if (isInverter(item)) {
      spec = `${item.capacityKW}kW cap · $${item.cost}`;
    } else {
      spec = `${item.storageKWh}kWh +${item.bonusKW}kW · $${item.cost}`;
    }
    this.add
      .text(x + w / 2, y + h - 8, spec, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.amberSoft,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1);
  }

  // ────────────────────────────────────────────────────────────────────────────
  private makeButton(
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const c = this.add.container(x, y);
    const bg = this.add.graphics();
    const paint = (hover: boolean) => {
      bg.clear();
      // Drop shadow
      bg.fillStyle(0x000000, 0.3);
      bg.fillRoundedRect(0, 3, w, h, 8);
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, 8);
      if (hover) {
        bg.lineStyle(2, BRAND.amber, 1);
        bg.strokeRoundedRect(0, 0, w, h, 8);
      }
    };
    paint(false);
    c.add(bg);
    const t = this.add
      .text(w / 2, h / 2, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '15px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(1);
    c.add(t);
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerover', () => paint(true));
    bg.on('pointerout', () => paint(false));
    bg.on('pointerdown', onClick);
    return c;
  }

  private makeTextLink(x: number, y: number, label: string, onClick: () => void) {
    const t = this.add
      .text(x, y, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '12px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerover', () => t.setColor(HEX.amber));
    t.on('pointerout', () => t.setColor(HEX.greyDim));
    t.on('pointerdown', onClick);
  }
}
